import { useEffect, useRef } from 'react';

import type {
  ExternalWorksBySegment,
  LinkedWorkRef,
  RouteSegment,
} from '../types/surveillanceRoute';

interface Args {
  routeId: string | undefined;
  /** Tramos que se están pintando (snapshot o servidor). */
  segments: RouteSegment[];
  /**
   * Mapa del dato que se pinta: el del servidor si llegó, `{}` si es snapshot.
   * `null` = el recorrido llegó pero sus trabajos no (sin red o el back fallando).
   */
  serverWorks: ExternalWorksBySegment | null | undefined;
  /** ¿Es el dato que se pinta el del servidor? `false` = snapshot de caché. */
  serverArrived: boolean;
}

interface Remembered {
  routeId: string;
  /** Por tramo: la OT del aviso con la que se calcularon sus trabajos. */
  bySegment: Record<string, { otId: string; works: LinkedWorkRef[] }>;
}

/**
 * Trabajos del aviso que no parpadean al revalidar.
 *
 * En cada revalidación el loader sirve el snapshot de caché, que no lleva el
 * mapa de trabajos (es una consulta sin caché). Sin esto, todas las pills
 * «Resuelto por» desaparecen hasta que responde el servidor y las filas saltan
 * bajo el dedo. Aquí se recuerda el ÚLTIMO mapa del servidor de ese recorrido y
 * se usa solo mientras llega el nuevo, y solo para los tramos cuya OT de aviso
 * sigue siendo la misma (si «Editar» cambió la OT, sus trabajos ya no valen).
 * Al cambiar de recorrido se olvida. Sin red (`fresh` = null) se sigue usando
 * lo último bueno, que es lo que el operario estaba viendo. Igual si el
 * recorrido se refresca pero sus trabajos no (`serverWorks` = null): «no se
 * sabe» no puede borrar las pills como si el servidor dijera que no hay.
 */
export function useStickyExternalWorks({
  routeId,
  segments,
  serverWorks,
  serverArrived,
}: Args): ExternalWorksBySegment {
  const remembered = useRef<Remembered | null>(null);
  const worksArrived = serverArrived && serverWorks !== null;

  useEffect(() => {
    if (!worksArrived || !routeId) return;
    const bySegment: Remembered['bySegment'] = {};
    for (const segment of segments) {
      const otId = segment.linkedExternalOrder?.id;
      const works = serverWorks?.[segment.id];
      if (otId && works) bySegment[segment.id] = { otId, works };
    }
    remembered.current = { routeId, bySegment };
  }, [routeId, segments, serverWorks, worksArrived]);

  if (worksArrived) return serverWorks ?? {};

  const last = remembered.current;
  if (!last || last.routeId !== routeId) return {};
  const reused: ExternalWorksBySegment = {};
  for (const segment of segments) {
    const entry = last.bySegment[segment.id];
    if (entry && entry.otId === segment.linkedExternalOrder?.id) {
      reused[segment.id] = entry.works;
    }
  }
  return reused;
}

export default useStickyExternalWorks;
